import {
  useState,
  useEffect,
  createContext,
  useMemo,
  useContext,
  useRef,
  useCallback,
} from "react";

import axios from "axios";
import LoginPage from "components/LoginPage/LoginPage";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { SnackbarContext } from "components/context/SnackbarProvider";
import qs from "qs";
import { set } from "react-hook-form";

export const AuthenticationContext = createContext(null);

export const SCOPES =
  "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/userinfo.email";

const HOST = process.env.REACT_APP_SERVICE_URL || "";
const HOST_RINGBACK = process.env.REACT_APP_BACKOFFICE_SERVICE_URL || "";

const AuthenticationProvider = ({ children }) => {
  const [token, setToken] = useState(undefined);
  const [googleToken, setGoogleToken] = useState(undefined);
  const { displayError } = useContext(SnackbarContext);

  const tokenRef = useRef(undefined);
  const refreshRef = useRef(undefined);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const logout = async () => {
    try {
      setToken(null);
      await axios.get(`${HOST}/api/logout/`, {
        withCredentials: true,
        crossDomain: true,
      });
    } catch (error) {
      if (error.response && error.response.status == 401) {
        displayError("Token not valid!");
      } else {
        displayError(error);
      }
    }
  };

  const loginFromCode = async (code) => {
    try {
      const response = await axios.get(
        `${HOST}/api/get_token_from_code/?code=${code}`,
        {
          withCredentials: true,
          crossDomain: true,
        },
      );
      setToken(response.data.access);
      setGoogleToken(response.data.google_token);
    } catch (error) {
      setToken(null);
      if (error.response && error.response.status == 401) {
        displayError("Token not valid!");
      } else {
        displayError(error);
      }
    }
  };

  const refreshLogin = () => {
    if (!refreshRef.current)
      refreshRef.current = axios.get(`${HOST}/api/token/refresh`, {
        withCredentials: true,
        crossDomain: true,
      });
    return refreshRef.current
      .then((response) => response.data.access)
      .finally(() => {
        refreshRef.current = undefined;
      });
  };

  const addAuthorisation = (config, token, host) => {
    config.baseURL = config.baseURL || host;
    config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  };

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  };

  const appAxios = useMemo(() => {
    if (token) {
      const appAxios = axios.create({
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });

      appAxios.interceptors.request.use((config) =>
        addAuthorisation(config, tokenRef.current, HOST),
      );

      appAxios.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          return refreshLogin()
            .then((token) => {
              setToken(token);
              return axios(addAuthorisation(error.config, token));
            })
            .catch((error) => {
              if (error.response.status == 401)
                // || error.response.status == 403)
                logout();
              throw error;
            });
        },
      );

      return appAxios;
    }
  }, [token]);

  const appAxiosRingback = useMemo(() => {
    if (token) {
      const appAxios = axios.create({
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      });

      appAxios.interceptors.request.use((config) => {
        let config2 = addAuthorisation(config, tokenRef.current, HOST_RINGBACK);
        config2.headers["X-CSRFToken"] = getCookie("csrftoken");
        return config2;
      });

      appAxios.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          if (error.response && error.response.status == 401) {
            return refreshLogin().then((token) => {
              setToken(token);
              return axios(addAuthorisation(error.config, token));
            });
          }
          throw error;
        },
      );

      appAxios.interceptors.response.use(
        (response) => {
          return response;
        },
        (error) => {
          if (
            !error.response ||
            error.response.status < 400 ||
            error.response.status >= 500
          ) {
            displayError("Error with backend !");
          }
          throw error;
        },
      );

      return appAxios;
    }
  }, [token]);

  useEffect(() => {
    refreshLogin()
      .then((token) => setToken(token))
      .catch((error) => setToken(null));
  }, []);

  let page;
  if (token === undefined) page = <></>;
  else if (token) page = children;
  else page = <LoginPage />;

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthenticationContext.Provider
        value={{
          logout,
          loginFromCode,
          appAxios,
          appAxiosRingback,
          googleToken,
        }}
      >
        {page}
      </AuthenticationContext.Provider>
    </GoogleOAuthProvider>
  );
};

export default AuthenticationProvider;
